import Cookies from 'js-cookie';
import { SnackbarProvider } from 'notistack';
import React, { useState, useRef, useEffect } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import { useRoutes } from 'react-router';
import {
  QueryCache,
  QueryClient,
  QueryClientProvider,
  keepPreviousData,
} from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import dayjs from 'dayjs';
import { X } from 'lucide';

// MUI
import { IconButton, Slide, ThemeProvider } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';

import 'dayjs/locale/en';
import 'dayjs/locale/vi';
import relativeTime from 'dayjs/plugin/relativeTime';
import timezone from 'dayjs/plugin/timezone';
import updateLocale from 'dayjs/plugin/updateLocale';
import utc from 'dayjs/plugin/utc';

// common
import { AUTHENTICATION_TOKEN, localeObject } from 'common/constants';
import './App.css';
import { AppState, store } from '@redux/store';
import useGeneralHook from 'common/hook/useGeneralHook';
import ConfirmDialogProvider from 'common/components/ConfirmDialogProvider';
import Icon from 'common/components/Icon';
import MessageDialogProvider from 'common/components/MessageDialogProvider';
import MuiLoading from 'common/components/MuiLoading';
import FetchErrorDialog from 'common/components/FetchErrorDialog';
import RedirectRoute from 'common/components/RedirectRoute';
import ProtectedRoute from 'common/components/ProtectedRoute';
import { MUITheme } from 'config/setupTheme';
import DefaultLayout from 'layout/main/DefaultLayout';
import { development } from 'api/API_App';
import { ROUTES_PATH } from 'layout/constants';

// screen
import LoginPage from 'modules/auth/page/LoginPage';
import { logoutFnc, revalidateAuthentication } from '@redux/authReducer';
import { setOn401Handler } from 'api/apiConfig';
import { setErrorNotifierRef } from 'common/errorNotifier';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(updateLocale);
dayjs.extend(relativeTime);
dayjs.updateLocale('en', localeObject);
dayjs.updateLocale('vi', localeObject);
dayjs.locale('vi');
const localeMap = {
  vi: 'vi',
  en: 'en',
};

function App() {
  const notiStackRef = useRef<{
    enqueueSnackbar: (opts: { message: unknown; variant: string }) => void;
    closeSnackbar: (key: unknown) => void;
  } | null>(null);
  const { dispatch } = useGeneralHook();

  const showError = (e: unknown) => {
    const err = e as {
      response?: { data?: { message?: string } };
      message?: string;
    };
    const message = err?.response?.data?.message ?? err?.message ?? 'An error occurred';
    notiStackRef.current?.enqueueSnackbar?.({ message, variant: 'error' });
  };

  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            gcTime: 0,
            refetchOnWindowFocus: false,
            retry: 0,
            placeholderData: keepPreviousData,
          },
          mutations: {
            onError: (e: unknown) => showError(e),
          },
        },
        queryCache: new QueryCache({
          onError: (e: unknown) => showError(e),
        }),
      })
  );
  const mode = useSelector((state: AppState) => state.cache.mode, shallowEqual);

  const routes = useRoutes([
    {
      path: ROUTES_PATH.login,
      element: (
        <RedirectRoute>
          <LoginPage />
        </RedirectRoute>
      ),
    },
    {
      path: '*',
      element: (
        <ProtectedRoute>
          <DefaultLayout />
        </ProtectedRoute>
      ),
    },
  ]);

  const locale = useSelector(({ intl }: AppState) => intl.locale, shallowEqual);

  const onClickDismiss = (key: React.Key) => () => {
    if (notiStackRef.current) notiStackRef.current.closeSnackbar(key);
  };

  useEffect(() => {
    setOn401Handler(() => store.dispatch(logoutFnc()));
    setErrorNotifierRef(
      notiStackRef as React.RefObject<{
        enqueueSnackbar: (opts: { message: string; variant: string }) => void;
      }>
    );
    return () => setErrorNotifierRef(null);
  }, []);

  useEffect(() => {
    if (Cookies.get(AUTHENTICATION_TOKEN)) {
      dispatch(revalidateAuthentication());
    } else {
      dispatch(logoutFnc());
    }
  }, [dispatch]);

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider theme={MUITheme(mode)}>
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={localeMap[locale]}>
          <SnackbarProvider
            ref={notiStackRef as any}
            maxSnack={3}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left',
            }}
            preventDuplicate
            autoHideDuration={2500}
            TransitionComponent={Slide}
            action={(key) => (
              <IconButton
                aria-label="Đóng"
                onClick={onClickDismiss(key)}
                sx={{ color: 'white' }}
              >
                <Icon icon={X} />
              </IconButton>
            )}
          >
            <ConfirmDialogProvider>
              <MessageDialogProvider>
                <React.Suspense fallback={<MuiLoading />}>{routes}</React.Suspense>
                <FetchErrorDialog />
              </MessageDialogProvider>
            </ConfirmDialogProvider>
          </SnackbarProvider>
        </LocalizationProvider>
      </ThemeProvider>
      {development && <ReactQueryDevtools />}
    </QueryClientProvider>
  );
}

export default App;
